<template>
  <DefaultModal
    :key="`${width}-${modalKey}`"
    :model-value="true"
    :fullscreen="width < 769"
    :title="title"
    :size="modalSize"
    :show-back-button="canGoBack"
    @update:model-value="closeModal"
    @back="goBack"
  >
    <template #body>
      <component
        :is="activeModalComponent"
        v-bind="activeModalProps"
        @update-user-data="updateUserData"
        @update:size="updateModalSize"
        @close="closeModal"
        @product-created="handleProductCreated"
        @group-created="handleGroupCreated"
        @category-created="handleCategoryCreated"
        @addon-created="handleAddonCreated"
        @ingredient-created="handleIngredientCreated"
      />
    </template>
  </DefaultModal>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useModalStore, modalStateEnum } from '@shared/model';
import { useGetWidth } from '@shared/lib';

import { modalBackNavigableStates } from '@shared/config';
import DefaultModal from '~/widgets/dynamic-modal/ui/DefaultModal.vue';

// Импорты всех компонентов модалок
import ModalOrderDetails from '~/widgets/order-modal/ui/ModalOrderDetails.vue';
import MoreAboutDish from '~/widgets/product-modal/ui/MoreAboutDish.vue';
import MoreAboutDishRefactored from '~/widgets/product-modal/ui/MoreAboutDishRefactored.vue';
import PhoneConfirmationInstructions from '~/features/auth/ui/PhoneConfirmationInstructions.vue';
import Login from '~/features/auth/ui/Login.vue';
import GoogleTowFA from '~/features/auth/ui/GoogleTowFA.vue';
import ChangePassword from '~/features/auth/ui/ChangePassword.vue';
import ChangePasswordSuccessfully from '~/features/auth/ui/ChangePasswordSuccessfully.vue';
import CodeFromEmail from '~/features/auth/ui/CodeFromEmail.vue';
import Registration from '~/features/auth/ui/Registration.vue';
import RegistrationSuccessfully from '~/features/auth/ui/RegistrationSuccessfully.vue';
import RestorePassword from '~/features/auth/ui/RestorePassword.vue';
import RestorePasswordSuccessfully from '~/features/auth/ui/RestorePasswordSuccessfully.vue';
import WelcomeBackInGoogle from '~/features/auth/ui/WelcomeBackInGoogle.vue';
import SetPassword from '~/features/auth/ui/SetPassword.vue';
import NumberConfirmation from '~/features/auth/ui/NumberConfirmation.vue';
import MaxNumberConfirmation from '~/features/auth/ui/MaxNumberConfirmation.vue';
import AdminLogin from '~/features/auth/ui/AdminLogin.vue';

// Password reset modals
import ForgotPassword from '~/features/auth/ui/ForgotPassword.vue';
import ResetPassword from '~/features/auth/ui/ResetPassword.vue';
import OrderVerification from '~/features/order-history/ui/OrderVerification.vue';
import YooKassaWidget from '~/features/payment/ui/YooKassaWidget.vue';
import TBusinessWidget from '~/features/payment/ui/TBusinessWidget.vue';
import AlfaBankWidget from '~/features/payment/ui/AlfaBankWidget.vue';
import LoyaltyPrivileges from '~/features/loyalty/ui/LoyaltyPrivileges.vue';
import ModalMapYandex from '~/features/map/ui/ModalMapYandex.vue';
import AddProductModal from '~/fsd-pages/admin/ui/modal/AddProductModal.vue';
import AddProductGroupModal from '~/fsd-pages/admin/ui/modal/AddProductGroupModal.vue';
import AddCategoryModal from '~/fsd-pages/admin/ui/modal/AddCategoryModal.vue';
import AddAddonModal from '~/fsd-pages/admin/ui/modal/AddAddonModal.vue';
import AddExcludeIngredientModal from '~/fsd-pages/admin/ui/modal/AddExcludeIngredientModal.vue';

// Settings modals
import EditInstitutionSettingsModal from '~/fsd-pages/admin/ui/modal/EditInstitutionSettingsModal.vue';
import EditFrontPadSettingsModal from '~/fsd-pages/admin/ui/modal/EditFrontPadSettingsModal.vue';
import EditPaymentSettingsModal from '~/fsd-pages/admin/ui/modal/EditPaymentSettingsModal.vue';
import EditEmailSettingsModal from '~/fsd-pages/admin/ui/modal/EditEmailSettingsModal.vue';
import EditTelegramSettingsModal from '~/fsd-pages/admin/ui/modal/EditTelegramSettingsModal.vue';
import EditVisualSettingsModal from '~/fsd-pages/admin/ui/modal/EditVisualSettingsModal.vue';
import EditYandexSettingsModal from '~/fsd-pages/admin/ui/modal/EditYandexSettingsModal.vue';
import EditNewsSliderSettingsModal from '~/fsd-pages/admin/ui/modal/EditNewsSliderSettingsModal.vue';
import EditLegalSettingsModal from '~/fsd-pages/admin/ui/modal/EditLegalSettingsModal.vue';
import EditBranchesSettingsModal from '~/fsd-pages/admin/ui/modal/EditBranchesSettingsModal.vue';

// Push notifications
import SendPushModal from '~/fsd-pages/admin/ui/modal/SendPushModal.vue';
import PushNotificationPermissionModal from '~/features/notifications/ui/PushNotificationPermissionModal.vue';

// Profile modals
import ModalDeleteProfile from '~/features/profile/ui/ModalDeleteProfile.vue';
import ModalDeliveryRules from '~/widgets/modal-delivery-rules/ui/ModalDeliveryRules.vue';
import PreorderTimeModal from '~/widgets/basket/ui/PreorderTimeModal.vue';
import OnboardingModal from '~/features/onboarding/ui/OnboardingModal.vue';

const props = defineProps({
  title: {
    type: String,
    default: '',
  },
  initialSize: {
    type: String,
    default: 's',
  },
});

const emit = defineEmits(['close']);

const { width } = useGetWidth();
// До onMounted composable отдаёт 0, а реальная ширина приезжает уже после монтирования
// детей — key прыгал «0-…» → «1440-…» и полностью перемонтировал содержимое сразу
// после открытия (терялось состояние, дублировались события аналитики карточки).
// Модалка рендерится только на клиенте (v-if по isOpen), так что ширина известна сразу.
if (import.meta.client && width.value === 0) width.value = window.innerWidth;
const modalStore = useModalStore();
const modalSize = ref(props.initialSize);

// Watch для изменения размера модального окна и принудительной перерисовки
const modalKey = ref(0);
watch(modalSize, () => {
  // Форсируем перерисовку модального окна при изменении размера
  modalKey.value++;
});

// Все доступные модалки с их типами и пропсами
const modalComponents = {
  [modalStateEnum.Login]: {
    component: Login,
    props: { userData: modalStore.modalData.userData },
    isAuth: true,
  },
  [modalStateEnum.AdminLogin]: {
    component: AdminLogin,
    props: {},
    isAuth: true,
  },
  [modalStateEnum.GoogleTowFA]: {
    component: GoogleTowFA,
    props: { userData: modalStore.modalData.userData },
    isAuth: true,
  },
  [modalStateEnum.Registration]: {
    component: Registration,
    props: { userData: modalStore.modalData.userData },
    isAuth: true,
  },
  [modalStateEnum.ChangePassword]: {
    component: ChangePassword,
    props: { userData: modalStore.modalData.userData },
    isAuth: true,
  },
  [modalStateEnum.RegistrationSuccessfully]: {
    component: RegistrationSuccessfully,
    props: { userData: modalStore.modalData.userData },
    isAuth: true,
  },
  [modalStateEnum.RestorePassword]: {
    component: RestorePassword,
    props: { userData: modalStore.modalData.userData },
    isAuth: true,
  },
  [modalStateEnum.ChangePasswordSuccessfully]: {
    component: ChangePasswordSuccessfully,
    props: { userData: modalStore.modalData.userData },
    isAuth: true,
  },
  [modalStateEnum.RestorePasswordSuccessfully]: {
    component: RestorePasswordSuccessfully,
    props: { userData: modalStore.modalData.userData },
    isAuth: true,
  },
  [modalStateEnum.SetPassword]: {
    component: SetPassword,
    props: { userData: modalStore.modalData.userData },
    isAuth: true,
  },
  [modalStateEnum.CodeFromEmail]: {
    component: CodeFromEmail,
    props: { userData: modalStore.modalData.userData },
    isAuth: true,
  },
  [modalStateEnum.WelcomeBackInGoogle]: {
    component: WelcomeBackInGoogle,
    props: { userData: modalStore.modalData.userData },
    isAuth: true,
  },

  // Password reset modals
  [modalStateEnum.ForgotPassword]: {
    component: ForgotPassword,
    props: { userData: modalStore.modalData.userData },
    isAuth: true,
  },
  [modalStateEnum.ResetPassword]: {
    component: ResetPassword,
    props: {
      userData: modalStore.modalData.userData,
      token: modalStore.modalData.token,
      user: modalStore.modalData.user,
    },
    isAuth: true,
  },

  [modalStateEnum.MoreAboutDish]: {
    component: MoreAboutDish,
    props: { formData: modalStore.modalData.formData },
    isAuth: false,
  },
  [modalStateEnum.MoreAboutDishRefactored]: {
    component: MoreAboutDishRefactored,
    props: { formData: modalStore.modalData.formData },
    isAuth: false,
  },
  // ✅ Обновлено: ModalOrderDetails (бывший ModalThankYou)
  [modalStateEnum.ModalOrderDetails]: {
    component: ModalOrderDetails,
    props: { formData: modalStore.modalData.formData },
    isAuth: false,
  },
  // ✅ Алиас для обратной совместимости
  [modalStateEnum.ModalThankYou]: {
    component: ModalOrderDetails,
    props: { formData: modalStore.modalData.formData },
    isAuth: false,
  },
  [modalStateEnum.PhoneConfirmationInstructions]: {
    component: PhoneConfirmationInstructions,
    props: { formData: modalStore.modalData.formData },
    isAuth: false,
  },
  [modalStateEnum.NumberConfirmation]: {
    component: NumberConfirmation,
    isAuth: false,
  },
  [modalStateEnum.MaxNumberConfirmation]: {
    component: MaxNumberConfirmation,
    isAuth: false,
  },
  [modalStateEnum.OrderVerification]: {
    component: OrderVerification,
    isAuth: false,
  },
  [modalStateEnum.YooKassaWidget]: {
    component: YooKassaWidget,
    isAuth: false,
  },
  [modalStateEnum.TBusinessWidget]: {
    component: TBusinessWidget,
    isAuth: false,
  },
  [modalStateEnum.AlfaBankWidget]: {
    component: AlfaBankWidget,
    isAuth: false,
  },
  [modalStateEnum.LoyaltyPrivileges]: {
    component: LoyaltyPrivileges,
    isAuth: false,
  },
  [modalStateEnum.ModalMapYandex]: {
    component: ModalMapYandex,
    props: {},
    isAuth: false,
  },
  [modalStateEnum.AddProduct]: {
    component: AddProductModal,
    props: {},
    isAuth: false,
  },
  [modalStateEnum.AddProductGroup]: {
    component: AddProductGroupModal,
    props: {},
    isAuth: false,
  },
  [modalStateEnum.AddCategory]: {
    component: AddCategoryModal,
    props: {},
    isAuth: false,
  },
  [modalStateEnum.AddAddon]: {
    component: AddAddonModal,
    props: {},
    isAuth: false,
  },
  [modalStateEnum.AddExcludeIngredient]: {
    component: AddExcludeIngredientModal,
    props: {},
    isAuth: false,
  },

  // Settings modals
  [modalStateEnum.EditInstitutionSettings]: {
    component: EditInstitutionSettingsModal,
    props: {},
    isAuth: false,
  },
  [modalStateEnum.EditFrontPadSettings]: {
    component: EditFrontPadSettingsModal,
    props: {},
    isAuth: false,
  },
  [modalStateEnum.EditPaymentSettings]: {
    component: EditPaymentSettingsModal,
    props: {},
    isAuth: false,
  },
  [modalStateEnum.EditEmailSettings]: {
    component: EditEmailSettingsModal,
    props: {},
    isAuth: false,
  },
  [modalStateEnum.EditTelegramSettings]: {
    component: EditTelegramSettingsModal,
    props: {},
    isAuth: false,
  },
  [modalStateEnum.EditVisualSettings]: {
    component: EditVisualSettingsModal,
    props: {},
    isAuth: false,
  },
  [modalStateEnum.EditYandexSettings]: {
    component: EditYandexSettingsModal,
    props: {},
    isAuth: false,
  },
  [modalStateEnum.EditNewsSliderSettings]: {
    component: EditNewsSliderSettingsModal,
    props: {},
    isAuth: false,
  },
  [modalStateEnum.EditLegalSettings]: {
    component: EditLegalSettingsModal,
    props: {},
    isAuth: false,
  },
  [modalStateEnum.EditBranchesSettings]: {
    component: EditBranchesSettingsModal,
    props: {},
    isAuth: false,
  },

  // Push notifications
  [modalStateEnum.SendPushNotification]: {
    component: SendPushModal,
    props: {},
    isAuth: false,
  },
  [modalStateEnum.PushNotificationPermission]: {
    component: PushNotificationPermissionModal,
    props: {},
    isAuth: false,
  },

  // Profile modals
  [modalStateEnum.DeleteProfile]: {
    component: ModalDeleteProfile,
    props: {},
    isAuth: false,
  },
  [modalStateEnum.DeliveryRules]: {
    component: ModalDeliveryRules,
    props: {},
    isAuth: false,
  },
  [modalStateEnum.PreorderTime]: {
    component: PreorderTimeModal,
    props: {},
    isAuth: false,
  },
  [modalStateEnum.Onboarding]: {
    component: OnboardingModal,
    props: {
      welcomePoints: modalStore.modalData.welcomePoints,
      hasAddress: modalStore.modalData.hasAddress,
    },
    isAuth: false,
  },
};

// Текущий активный компонент и его пропсы
const activeModalComponent = computed(() => {
  return modalComponents[modalStore.currentState as keyof typeof modalComponents]?.component;
});

const activeModalProps = computed(() => {
  const modalConfig = modalComponents[modalStore.currentState as keyof typeof modalComponents];
  return (modalConfig as any)?.props || {};
});

const canGoBack = computed(() => {
  return modalStore.previousStates.length > 0 && modalBackNavigableStates.includes(modalStore.currentState);
});

const updateModalSize = (size: string) => {
  modalSize.value = size;
};

const updateUserData = (data: any) => {
  modalStore.updateModalData({ userData: data });
};

const goBack = () => {
  modalStore.back();
};

const closeModal = () => {
  modalStore.close();
  emit('close');
};

const handleProductCreated = () => {
  closeModal();
};

const handleGroupCreated = () => {
  closeModal();
};

const handleCategoryCreated = () => {
  closeModal();
};

const handleAddonCreated = () => {
  closeModal();
};

const handleIngredientCreated = () => {
  closeModal();
};
</script>